"use client";

import React, { useCallback, useRef, useEffect, useState } from "react";
import {
  StandardFormInput,
  StandardFormTagInput,
} from "@/components/common/StandardFormInput";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { Plus, X, Clock } from "lucide-react";

export type VariantOption = {
  id: string;
  title: string;
  values: string[];
};

type Props = {
  options: VariantOption[];
  onChange: (options: VariantOption[]) => void;
  maxOptions?: number;
};

export default function VariantOptions({
  options,
  onChange,
  maxOptions = 3,
}: Props) {
  // Refs for debouncing option values changes
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingChangesRef = useRef<VariantOption[]>(options);
  const [isGeneratingVariants, setIsGeneratingVariants] = useState(false);

  // Update pending changes whenever options change
  useEffect(() => {
    pendingChangesRef.current = options;
  }, [options]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Debounced change handler specifically for option values
  const debouncedOnChange = useCallback((newOptions: VariantOption[]) => {
    console.log('⏰ Debounced onChange triggered, setting timer...');
    // Update pending changes immediately
    pendingChangesRef.current = newOptions;
    
    // Show generating state
    setIsGeneratingVariants(true);
    
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set a new timer for the actual onChange call
    debounceTimerRef.current = setTimeout(() => {
      console.log('🚀 Debounced timer fired, calling onChange with:', pendingChangesRef.current.map(opt => ({ title: opt.title, values: opt.values })));
      onChange(pendingChangesRef.current);
      setIsGeneratingVariants(false);
      debounceTimerRef.current = null;
    }, 500);
  }, [onChange]);

  const addOption = () => {
    if (options.length >= maxOptions) return;

    const newOption: VariantOption = {
      id: Date.now().toString(),
      title: "",
      values: [],
    };

    // Clear any pending debounced changes and generating state
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setIsGeneratingVariants(false);

    // For adding/removing options, call onChange immediately (no debounce)
    const newOptions = [...options, newOption];
    onChange(newOptions);
  };

  const removeOption = (id: string) => {
    // Clear any pending debounced changes and generating state
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setIsGeneratingVariants(false);

    // For adding/removing options, call onChange immediately (no debounce)
    const newOptions = options.filter((option) => option.id !== id);
    onChange(newOptions);
  };

  const updateOption = (id: string, field: keyof VariantOption, value: any) => {
    const newOptions = options.map((option) =>
      option.id === id ? { ...option, [field]: value } : option,
    );

    console.log('🎛️ VariantOptions.updateOption:', {
      id,
      field,
      value,
      newOptions: newOptions.map(opt => ({ title: opt.title, values: opt.values }))
    });

    // If updating values, use debounced handler
    if (field === 'values') {
      console.log('⏱️ Using debounced onChange for values');
      debouncedOnChange(newOptions);
    } else {
      // For title changes, update immediately
      console.log('⚡ Using immediate onChange for title');
      onChange(newOptions);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-foreground">
            Variant Options
          </h3>
          <p className="text-xs text-muted-foreground">
            Define up to {maxOptions} option groups (e.g., Color, Size) and
            their values
          </p>
        </div>
        {options.length < maxOptions && (
          <ButtonPrimary type="button" onClick={addOption} size="sm">
            <Plus size={16} className="mr-1" />
            Add Option Group
          </ButtonPrimary>
        )}
      </div>

      {options.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
          <p className="text-sm text-muted-foreground">
            No variant options yet. Add an option group to get started.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {options.map((option, index) => (
          <div
            key={option.id}
            className="p-4 border border-border rounded-lg bg-card"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-foreground">
                Option Group {index + 1}
              </span>
              <button
                type="button"
                onClick={() => removeOption(option.id)}
                className="text-muted-foreground hover:text-destructive transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3">
              <StandardFormInput
                label="Option Name"
                type="text"
                placeholder="e.g., Color, Size, Material"
                value={option.title}
                onChange={(e) =>
                  updateOption(option.id, "title", e.target.value)
                }
              />

              <StandardFormTagInput
                label="Option Values"
                tags={option.values}
                onTagsChange={(values) =>
                  updateOption(option.id, "values", values)
                }
                placeholder="Enter values, press Enter to add"
              />

              {option.values.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  {option.values.length} value
                  {option.values.length !== 1 ? "s" : ""}:{" "}
                  {option.values.join(", ")}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {options.length > 0 && (
        <div className="mt-4 p-3 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            {isGeneratingVariants && (
              <Clock size={12} className="animate-spin text-primary" />
            )}
            <strong>Preview:</strong> 
            {isGeneratingVariants ? (
              "Generating variants..."
            ) : (
              <>
                This will generate{" "}
                {options.reduce(
                  (total, option) => total * (option.values.length || 1),
                  1,
                )}{" "}
                variant combinations when all option groups have values.
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
